import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useOptionalI18n } from '../../i18n/I18nProvider';
import { shouldUseMobileRuntimeFeatures } from '../utils/uiMode';

const noop = () => {};

function makeChangeEvent(value, name) {
  const target = { value, name };
  return { target, currentTarget: target };
}

function clampNumber(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.max(min, Math.min(max, n));
}

function getMobileInputKeyboardInset(baseHeightRef) {
  if (typeof window === 'undefined') return 0;

  try {
    const vv = window.visualViewport;
    if (!vv) return 0;

    const currentInnerHeight = Number(window.innerHeight || 0);
    const currentViewportHeight = Number(vv.height || 0);
    const currentOffsetTop = Number(vv.offsetTop || 0);

    if (!Number.isFinite(baseHeightRef.current) || baseHeightRef.current <= 0) {
      baseHeightRef.current = Math.max(currentInnerHeight, currentViewportHeight, 1);
    }

    // iOS/Android 모두 대응하기 위해 input focus 전의 안정 높이를 기준으로 키보드가 덮은 높이를 계산한다.
    // 일부 Android WebView/Chrome은 키보드가 열리면 window.innerHeight도 같이 줄어들기 때문에
    // 매번 현재 innerHeight로 계산하면 keyboard inset이 0으로 나올 수 있다.
    const keyboard = Number(baseHeightRef.current || 0) - currentViewportHeight - currentOffsetTop;
    const upperLimit = Math.max(0, Number(baseHeightRef.current || 0) * 0.72);
    return Math.round(clampNumber(keyboard, 0, upperLimit));
  } catch (_) {
    return 0;
  }
}

function freezeMobileInputViewport() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }

  const root = document.documentElement;
  const body = document.body;
  const countKey = '__npcMobileInputViewportFreezeCount';
  const releaseTimerKey = '__npcMobileInputViewportFreezeReleaseTimer';
  const bodyLockKey = '__npcMobileInputBodyLockCount';
  const bodyStyleKey = '__npcMobileInputBodyPrevStyles';

  try { clearTimeout(window[releaseTimerKey]); } catch (_) {}

  window[countKey] = Number(window[countKey] || 0) + 1;
  root.dataset.npcMobileInputViewportFreeze = '1';

  if (Number(window[bodyLockKey] || 0) <= 0) {
    const scrollY = Number(window.scrollY || window.pageYOffset || 0);
    window[bodyStyleKey] = {
      scrollY,
      htmlOverflow: root.style.overflow,
      htmlOverscroll: root.style.overscrollBehavior,
      htmlTouchAction: root.style.touchAction,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
      bodyOverscroll: body.style.overscrollBehavior,
      bodyTouchAction: body.style.touchAction,
    };

    root.style.overflow = 'hidden';
    root.style.overscrollBehavior = 'none';
    root.style.touchAction = 'none';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overscrollBehavior = 'none';
    body.style.touchAction = 'none';
  }
  window[bodyLockKey] = Number(window[bodyLockKey] || 0) + 1;

  return () => {
    const nextFreeze = Math.max(0, Number(window[countKey] || 0) - 1);
    window[countKey] = nextFreeze;

    const nextBodyLock = Math.max(0, Number(window[bodyLockKey] || 0) - 1);
    window[bodyLockKey] = nextBodyLock;

    if (nextBodyLock <= 0) {
      const prev = window[bodyStyleKey] || {};
      root.style.overflow = prev.htmlOverflow || '';
      root.style.overscrollBehavior = prev.htmlOverscroll || '';
      root.style.touchAction = prev.htmlTouchAction || '';
      body.style.overflow = prev.bodyOverflow || '';
      body.style.position = prev.bodyPosition || '';
      body.style.top = prev.bodyTop || '';
      body.style.left = prev.bodyLeft || '';
      body.style.right = prev.bodyRight || '';
      body.style.width = prev.bodyWidth || '';
      body.style.overscrollBehavior = prev.bodyOverscroll || '';
      body.style.touchAction = prev.bodyTouchAction || '';
      try { window.scrollTo(0, Number(prev.scrollY || 0)); } catch (_) {}
      try { delete window[bodyStyleKey]; } catch (_) { window[bodyStyleKey] = null; }
    }

    if (nextFreeze <= 0) {
      // 키보드 닫힘 애니메이션 도중 visualViewport resize가 한 번 더 들어올 수 있어
      // 짧게 freeze를 유지했다가 해제한다.
      window[releaseTimerKey] = setTimeout(() => {
        if (Number(window[countKey] || 0) > 0) return;
        try { delete root.dataset.npcMobileInputViewportFreeze; } catch (_) { root.dataset.npcMobileInputViewportFreeze = ''; }
      }, 380);
    }
  };
}

/**
 * MobileInputModal
 *
 * 모바일 전용 입력 안정화 컴포넌트.
 * - 화면 안의 input은 직접 포커스하지 않고 readOnly 표시 필드/버튼으로 사용한다.
 * - 터치 시 검정 반투명 오버레이 + 입력 전용 모달을 띄운다.
 * - 모달이 닫힐 때 입력값을 기존 onChange/onValueChange로 반영한다.
 */
export default function MobileInputModal({
  value = '',
  onChange = noop,
  onValueChange,
  onCommit,
  className,
  style,
  placeholder = '',
  disabled = false,
  name,
  id,
  inputMode,
  autoComplete = 'off',
  spellCheck = false,
  type = 'text',
  maxLength,
  min,
  max,
  step,
  modalTitle,
  modalDescription,
  completeLabel,
  multiline = false,
  rows = 3,
  modalInputType,
  ariaLabel,
  triggerAsButton = false,
}) {
  const { t } = useOptionalI18n();
  const resolvedModalTitle = modalTitle || t('common.mobileInput.title', null, '텍스트 입력');
  const resolvedModalDescription = modalDescription || t('common.mobileInput.guide', null, '입력할 내용을 작성해주세요.');
  const resolvedCompleteLabel = completeLabel || t('common.doneWriting', null, '작성 완료');

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));
  const [keyboardInset, setKeyboardInset] = useState(0);
  const inputRef = useRef(null);
  const keyboardBaseHeightRef = useRef(0);
  const openedAtRef = useRef(0);
  const realMobileRuntime = shouldUseMobileRuntimeFeatures();

  useEffect(() => {
    if (!realMobileRuntime || !open) return;
    setDraft(String(value ?? ''));
  }, [open, realMobileRuntime, value]);

  useEffect(() => {
    if (!realMobileRuntime || !open) return undefined;
    keyboardBaseHeightRef.current = 0;
    try {
      const vv = window.visualViewport;
      keyboardBaseHeightRef.current = Math.max(
        Number(window.innerHeight || 0),
        Number(vv?.height || 0),
        Number(document.documentElement?.clientHeight || 0),
        1
      );
    } catch (_) {
      keyboardBaseHeightRef.current = 0;
    }

    const releaseFreeze = freezeMobileInputViewport();
    const timer = window.setTimeout(() => {
      try { inputRef.current?.focus?.({ preventScroll: true }); } catch (_) { try { inputRef.current?.focus?.(); } catch (__) {} }
      try { inputRef.current?.select?.(); } catch (_) {}
    }, 45);

    return () => {
      window.clearTimeout(timer);
      releaseFreeze?.();
      keyboardBaseHeightRef.current = 0;
      setKeyboardInset(0);
    };
  }, [open, realMobileRuntime]);

  useEffect(() => {
    if (!realMobileRuntime || !open) return undefined;
    if (typeof window === 'undefined') return undefined;

    const vv = window.visualViewport;
    let raf = null;

    const update = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const next = getMobileInputKeyboardInset(keyboardBaseHeightRef);
        setKeyboardInset((prev) => (prev === next ? prev : next));
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    if (vv) {
      vv.addEventListener('resize', update);
      vv.addEventListener('scroll', update);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      if (vv) {
        try { vv.removeEventListener('resize', update); } catch (_) {}
        try { vv.removeEventListener('scroll', update); } catch (_) {}
      }
    };
  }, [open, realMobileRuntime]);

  const openModal = useCallback((e) => {
    if (disabled) return;
    e?.preventDefault?.();
    e?.stopPropagation?.();
    openedAtRef.current = Date.now();
    setOpen(true);
  }, [disabled]);

  const commitAndClose = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const next = String(draft ?? '');
    onValueChange?.(next);
    onChange?.(makeChangeEvent(next, name));
    onCommit?.(next);
    setOpen(false);
  }, [draft, name, onChange, onCommit, onValueChange]);

  const handleBackdropClick = useCallback((e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();

    // Android Chrome/WebView can dispatch a delayed compatibility click from the
    // original trigger tap after the modal has already been mounted. If the
    // backdrop closes on that first ghost click, the modal appears and then
    // immediately disappears unless the user long-presses. Ignore backdrop
    // clicks briefly after opening and only close from a deliberate later tap.
    if (Date.now() - Number(openedAtRef.current || 0) < 650) {
      return;
    }

    commitAndClose(e);
  }, [commitAndClose]);

  const stopModalEvent = useCallback((e) => {
    e?.stopPropagation?.();
  }, []);


  // 데스크톱/일반 브라우저에서는 모바일 전용 입력 모달을 쓰지 않는다.
  // 단, Hooks는 위에서 항상 같은 순서로 호출한 뒤에 분기해야 React Hooks 순서 오류가 나지 않는다.
  if (!realMobileRuntime) {
    const nativeValue = String(value ?? '');
    const handleNativeChange = (event) => {
      const next = event?.target?.value ?? '';
      onValueChange?.(next);
      onChange?.(event);
    };

    if (multiline) {
      return (
        <textarea
          id={id}
          name={name}
          className={className}
          style={style}
          value={nativeValue}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          maxLength={maxLength}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          aria-label={ariaLabel || resolvedModalTitle}
          onChange={handleNativeChange}
          onBlur={(e) => onCommit?.(e.target.value)}
        />
      );
    }

    return (
      <input
        id={id}
        name={name}
        className={className}
        style={style}
        value={nativeValue}
        placeholder={placeholder}
        disabled={disabled}
        inputMode={inputMode}
        type={type}
        maxLength={maxLength}
        min={min}
        max={max}
        step={step}
        autoComplete={autoComplete}
        spellCheck={spellCheck}
        aria-label={ariaLabel || resolvedModalTitle}
        onChange={handleNativeChange}
        onBlur={(e) => onCommit?.(e.target.value)}
      />
    );
  }

  const modal = open ? createPortal(
    <div
      style={{
        ...overlayStyle,
        paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 16px + ${keyboardInset}px)`,
      }}
      role="presentation"
      onClick={handleBackdropClick}
    >
      <div
        style={sheetStyle}
        role="dialog"
        aria-modal="true"
        aria-label={resolvedModalTitle}
        onClick={stopModalEvent}
        onMouseDown={stopModalEvent}
        onTouchStart={stopModalEvent}
      >
        <div style={titleStyle}>{resolvedModalTitle}</div>
        {resolvedModalDescription ? <div style={descriptionStyle}>{resolvedModalDescription}</div> : null}
        {multiline ? (
          <textarea
            ref={inputRef}
            style={{ ...editorStyle, ...textareaStyle }}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={placeholder}
            rows={rows}
            maxLength={maxLength}
            autoComplete={autoComplete}
            spellCheck={spellCheck}
          />
        ) : (
          <input
            ref={inputRef}
            style={editorStyle}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitAndClose(e);
            }}
            placeholder={placeholder}
            inputMode={inputMode}
            type={modalInputType || (type === 'password' ? 'password' : 'text')}
            maxLength={maxLength}
            min={min}
            max={max}
            step={step}
            autoComplete={autoComplete}
            spellCheck={spellCheck}
          />
        )}
        <div style={actionRowStyle}>
          <button type="button" style={completeButtonStyle} onClick={commitAndClose}>
            {resolvedCompleteLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  const displayValue = String(value ?? '');
  const triggerLabel = displayValue
    ? (type === 'password' ? '•'.repeat(Math.min(displayValue.length, 12)) : displayValue)
    : placeholder;

  return (
    <>
      {triggerAsButton ? (
        <button
          id={id}
          name={name}
          type="button"
          className={className}
          style={{
            ...(style || {}),
            textAlign: 'left',
            WebkitAppearance: 'none',
            appearance: 'none',
            userSelect: 'none',
          }}
          disabled={disabled}
          aria-label={ariaLabel || resolvedModalTitle}
          onClick={openModal}
          onMouseDown={openModal}
          onTouchStart={openModal}
        >
          <span
            style={{
              display: 'block',
              width: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: displayValue ? 'inherit' : 'rgba(255,255,255,0.45)',
              pointerEvents: 'none',
            }}
          >
            {triggerLabel}
          </span>
        </button>
      ) : (
        <input
          id={id}
          name={name}
          className={className}
          style={style}
          value={displayValue}
          placeholder={placeholder}
          disabled={disabled}
          readOnly
          inputMode={inputMode}
          type={type === 'password' ? 'password' : 'text'}
          maxLength={maxLength}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          aria-label={ariaLabel || resolvedModalTitle}
          onClick={openModal}
          onFocus={openModal}
          onMouseDown={openModal}
          onTouchStart={openModal}
        />
      )}
      {modal}
    </>
  );
}

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 2147483000,
  background: 'rgba(0,0,0,0.60)',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'center',
  padding: '16px 12px 16px',
  boxSizing: 'border-box',
  touchAction: 'none',
};

const sheetStyle = {
  width: 'min(430px, 100%)',
  maxHeight: 'min(460px, calc(100vh - 32px))',
  overflowY: 'auto',
  WebkitOverflowScrolling: 'touch',
  background: 'rgba(38,38,38,0.98)',
  border: '1px solid rgba(255,255,255,0.16)',
  borderRadius: 18,
  boxShadow: '0 18px 70px rgba(0,0,0,0.55)',
  color: '#fff',
  padding: '22px 20px 18px',
  boxSizing: 'border-box',
  touchAction: 'auto',
};

const titleStyle = {
  fontSize: 18,
  lineHeight: 1.25,
  fontWeight: 900,
  color: '#fff',
  marginBottom: 10,
};

const descriptionStyle = {
  fontSize: 15,
  lineHeight: 1.38,
  fontWeight: 600,
  color: 'rgba(255,255,255,0.9)',
  whiteSpace: 'pre-line',
  marginBottom: 18,
};

const editorStyle = {
  width: '100%',
  height: 44,
  lineHeight: '44px',
  border: 'none',
  borderBottom: '3px solid rgba(255,255,255,0.86)',
  outline: 'none',
  borderRadius: 0,
  background: 'transparent',
  color: '#fff',
  fontSize: 20,
  WebkitTextSizeAdjust: '100%',
  fontWeight: 700,
  padding: '0 2px',
  boxSizing: 'border-box',
};

const textareaStyle = {
  minHeight: 98,
  lineHeight: 1.35,
  resize: 'none',
  paddingTop: 8,
};

const actionRowStyle = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 10,
  marginTop: 18,
};

const completeButtonStyle = {
  height: 44,
  minWidth: 108,
  padding: '0 18px',
  border: '1px solid rgba(0,0,0,0.35)',
  borderRadius: 10,
  background: '#f5c51b',
  color: '#111',
  fontSize: 16,
  fontWeight: 900,
  cursor: 'pointer',
};
